'use client';

import React, { useState, useEffect, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import AppLogo from '@/components/ui/AppLogo';
import { Home, Search, Shield, Star } from 'lucide-react';

function AuthContentInner() {
  const searchParams = useSearchParams();
  const tabParam = searchParams?.get('tab');
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>(
    tabParam === 'signup' ? 'signup' : 'login'
  );

  useEffect(() => {
    if (tabParam === 'signup') setActiveTab('signup');
    else if (tabParam === 'login') setActiveTab('login');
  }, [tabParam]);

  return (
    <div className="min-h-screen flex">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative bg-primary overflow-hidden flex-col">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full border border-white" />
          <div className="absolute top-32 left-32 w-40 h-40 rounded-full border border-white" />
          <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full border border-white" />
          <div className="absolute bottom-40 right-40 w-48 h-48 rounded-full border border-white" />
        </div>

        <div className="relative z-10 flex flex-col h-full p-12 xl:p-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3">
            <AppLogo size={44} />
            <span className="font-extrabold text-2xl text-white tracking-tight">
              Tolettu.com
            </span>
          </Link>

          {/* Hero Copy */}
          <div className="flex-1 flex flex-col justify-center mt-8">
            <h1 className="text-hero text-white mb-4 leading-tight">
              Find your perfect<br />
              <span className="text-accent">rental home</span><br />
              in India
            </h1>
            <p className="text-white/70 text-lg font-medium max-w-md leading-relaxed">
              Browse thousands of verified rental properties — apartments, villas, independent houses, and rooms across India&apos;s top cities.
            </p>

            {/* Feature Pills */}
            <div className="flex flex-col gap-3 mt-10">
              {[
                { icon: Shield, text: 'All listings owner-verified before going live' },
                { icon: Search, text: 'Filter by city, BHK, budget, and furnishing' },
                { icon: Home, text: 'Direct owner contact — no broker, no commission' },
                { icon: Star, text: 'Photos, specs, map location for every listing' },
              ]?.map((feature, i) => (
                <div key={`feature-${i + 1}`} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                    <feature.icon size={16} className="text-white" />
                  </div>
                  <span className="text-white/80 text-sm font-medium">{feature?.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Stats */}
          <div className="grid grid-cols-3 gap-4 pt-8 border-t border-white/20">
            {[
              { value: '12,400+', label: 'Active Listings' },
              { value: '38 Cities', label: 'Across India' },
              { value: '4.8★', label: 'Owner Rating' },
            ]?.map((stat) => (
              <div key={`stat-${stat?.label}`}>
                <div className="text-xl font-extrabold text-white tabular-nums">{stat?.value}</div>
                <div className="text-xs text-white/60 font-medium mt-0.5">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 sm:px-10 lg:px-12 xl:px-16 bg-background overflow-y-auto">
        {/* Mobile Logo */}
        <Link href="/" className="lg:hidden flex items-center gap-2.5 mb-8">
          <AppLogo size={36} />
          <span className="font-extrabold text-xl text-foreground">
            tolettu<span className="text-primary">.com</span>
          </span>
        </Link>

        <div className="w-full max-w-md mx-auto">
          {/* Tab Switcher */}
          <div className="flex p-1 bg-muted rounded-2xl mb-8">
            <button
              suppressHydrationWarning
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${
                activeTab === 'login' ?'bg-card text-foreground card-shadow' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              suppressHydrationWarning
              onClick={() => setActiveTab('signup')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${
                activeTab === 'signup' ?'bg-card text-foreground card-shadow' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Create Account
            </button>
          </div>

          {/* Forms */}
          {activeTab === 'login' ? (
            <LoginForm onSwitchToSignup={() => setActiveTab('signup')} />
          ) : (
            <SignupForm onSwitchToLogin={() => setActiveTab('login')} />
          )}
        </div>
      </div>
    </div>
  );
}

export default function AuthContent() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    }>
      <AuthContentInner />
    </Suspense>
  );
}